import { useTranslation } from "react-i18next";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { Select, SelectOption, Slider, Switcher } from "@/shared/ui";
import PlayerMenuControlLabel from "@/domains/playerMenu/components/PlayerMenuControlLabel";
import PlayerMenuTooltip from "@/domains/playerMenu/components/PlayerMenuTooltip";
import useChatValues from "@/domains/settings/chat/hooks/useChatValues";
import { appliedChatSettingsSelector, setAppliedSettings } from "@/domains/settings/chat/slice";

export default function PlayerMenuSettingsChatCommonContainer() {
  const { t } = useTranslation("chat");
  const dispatch = useAppDispatch();
  const {
    fontSizeValues,
    fontWeightValues,
    opacityTextValues,
    chatHeightSizeValues,
    chatWidthSizeValues,
    opacityBackgroundValues,
  } = useChatValues();
  const {
    fontSize,
    fontWeight,
    opacityText,
    chatHeightSize,
    chatWidthSize,
    opacityBackground,
    opacityChatModeWithoutAction,
    showCommandTooltip,
    notifyInRolledUpMode,
  } = useAppSelector(appliedChatSettingsSelector);

  return (
    <div className="grid grid-cols-2 gap-5">
      <div className="flex flex-col gap-5">
        <div>
          <PlayerMenuControlLabel>{t("chat.settings.fontSize")}</PlayerMenuControlLabel>
          <Slider
            value={fontSize}
            values={fontSizeValues}
            min={12}
            max={24}
            step={2}
            onChange={(value) => dispatch(setAppliedSettings({ fontSize: value }))}
            className="mt-3"
            classNameSlider="mt-3"
            classNameLabel="text-12 font-bold uppercase"
          />
        </div>
        <div>
          <PlayerMenuControlLabel>{t("chat.settings.fontWeight")}</PlayerMenuControlLabel>
          <Select value={fontWeight} className="mt-3 z-3" bg="bg-dark-800">
            <div className="flex flex-col">
              {fontWeightValues.map((item) => (
                <SelectOption
                  key={item.value}
                  onClick={() => dispatch(setAppliedSettings({ fontWeight: item.value }))}
                  isSelected={item.value === fontWeight}
                  className="hover:text-accent-solid transition-all"
                >
                  {t(item.label)}
                </SelectOption>
              ))}
            </div>
          </Select>
        </div>
        <div>
          <PlayerMenuControlLabel>{t("chat.settings.opacityText")}</PlayerMenuControlLabel>
          <Slider
            value={opacityText}
            values={opacityTextValues}
            min={0}
            max={100}
            step={5}
            onChange={(value) => dispatch(setAppliedSettings({ opacityText: value }))}
            className="mt-3"
            classNameSlider="mt-3"
            classNameLabel="text-12 font-bold uppercase"
          />
        </div>
        <div>
          <PlayerMenuControlLabel>{t("chat.settings.chatHeight")}</PlayerMenuControlLabel>
          <Slider
            value={chatHeightSize}
            values={chatHeightSizeValues}
            min={0}
            max={100}
            step={1}
            onChange={(value) => dispatch(setAppliedSettings({ chatHeightSize: value }))}
            className="mt-3"
            classNameSlider="mt-3"
            classNameLabel="text-12 font-bold uppercase"
          />
        </div>
        <div>
          <PlayerMenuControlLabel>{t("chat.settings.chatWidth")}</PlayerMenuControlLabel>
          <Slider
            value={chatWidthSize}
            values={chatWidthSizeValues}
            min={0}
            max={100}
            step={1}
            onChange={(value) => dispatch(setAppliedSettings({ chatWidthSize: value }))}
            className="mt-3"
            classNameSlider="mt-3"
            classNameLabel="text-12 font-bold uppercase"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <PlayerMenuControlLabel>{t("chat.settings.opacityBackground")}</PlayerMenuControlLabel>
            <PlayerMenuTooltip text={t("chat.settings.opacityBackgroundTooltip")} />
          </div>
          <Slider
            value={opacityBackground}
            values={opacityBackgroundValues}
            min={0}
            max={100}
            step={5}
            onChange={(value) => dispatch(setAppliedSettings({ opacityBackground: value }))}
            className="mt-3"
            classNameSlider="mt-3"
            classNameLabel="text-12 font-bold uppercase"
          />
        </div>
      </div>
      <div className="w-full h-fit flex flex-col rounded-[0.625rem] bg-dark-800 border border-dark-700/20">
        <div className="h-[3.125rem] px-3.5 flex items-center justify-between">
          <div className="text-14 font-bold">{t("chat.settings.opacityChatModeWithoutAction")}</div>
          <PlayerMenuTooltip
            text={t("chat.settings.opacityChatModeWithoutActionTooltip")}
            className="ml-auto"
          />
          <Switcher
            on={opacityChatModeWithoutAction}
            onChange={() =>
              dispatch(
                setAppliedSettings({ opacityChatModeWithoutAction: !opacityChatModeWithoutAction }),
              )
            }
            withBorder
          />
        </div>
        <div className="h-[3.125rem] px-3.5 flex items-center justify-between border-t border-dark-700/20">
          <div className="text-14 font-bold">{t("chat.settings.showCommand")}</div>
          <PlayerMenuTooltip text={t("chat.settings.showCommandTooltip")} className="ml-auto" />
          <Switcher
            on={showCommandTooltip}
            onChange={() =>
              dispatch(setAppliedSettings({ showCommandTooltip: !showCommandTooltip }))
            }
            withBorder
          />
        </div>
        <div className="h-[3.125rem] px-3.5 flex items-center justify-between border-t border-dark-700/20">
          <div className="text-14 font-bold">{t("chat.settings.opacityChatModeWithoutAction")}</div>
          <PlayerMenuTooltip
            text={t("chat.settings.opacityChatModeWithoutActionTooltip")}
            className="ml-auto"
          />
          <Switcher
            on={notifyInRolledUpMode}
            onChange={() =>
              dispatch(setAppliedSettings({ notifyInRolledUpMode: !notifyInRolledUpMode }))
            }
            withBorder
          />
        </div>
      </div>
    </div>
  );
}
